import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import axios from "../../../utils/axios";
import { useDialog } from "../../../contexts/DialogContext";
import { showErrorSnackbar } from "../../../redux/slices/snackbar";
import { getTimeStamp } from "../../../utils/common";
import DeleteBackupViewer, { formatDate, parseJson } from "../../../components/DeleteBackupViewer";
import useWindowDimensions from "../../../utils/windowDimensions";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

function DeleteHistoryDetail({ historyId }) {
  const dispatch = useDispatch();
  const [history, setHistory] = useState(null);
  const historyUrl = "/cpanel/tenant/delete-history/" + historyId;

  useEffect(() => {
    axios
      .get(historyUrl)
      .then((response) => setHistory(response.data))
      .catch((err) => dispatch(showErrorSnackbar(err.message || "Failed to load history")));
  }, [historyId]);

  if (!history) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DeleteBackupViewer
      historyUrl={historyUrl}
      info={[
        ["Franchise", `${history.tenantName} (ID ${history.tenantId})`],
        ["Business Type", history.businessType],
        ["Deleted By", history.deletedBy],
        ["Deleted At", formatDate(history.deletedAt)],
        ["IP Address", history.ipAddress],
      ]}
      summary={parseJson(history.summary, [])}
      totalRows={history.totalRows}
      fileName={`franchise-backup-${history.tenantId}-${historyId}.json`}
    />
  );
}

function DeleteHistory() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openDialog } = useDialog();
  const { height } = useWindowDimensions();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get("/cpanel/tenant/delete-history", {
        params: { search, page: paginationModel.page, size: paginationModel.pageSize },
      });
      setRows(response.data.content);
      setRowCount(response.data.totalElements);
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || "Failed to load delete history"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchData, 300);
    return () => clearTimeout(timer);
  }, [paginationModel, search]);

  const openDetail = (historyId) =>
    openDialog(
      "tenant_delete_history",
      "Deleted Franchise Details",
      <DeleteHistoryDetail key={getTimeStamp()} historyId={historyId} />,
      "md",
    );

  const columns = [
    {
      field: "tenantName",
      headerName: "Franchise Name",
      width: 220,
      renderCell: (params) => (
        <Button size="small" onClick={() => openDetail(params.row.tenantDeleteHistoryId)}>
          {params.value}
        </Button>
      ),
    },
    { field: "tenantId", headerName: "Franchise ID", width: 110 },
    { field: "businessType", headerName: "Business Type", width: 220 },
    { field: "userName", headerName: "User Name", width: 160 },
    { field: "deletedBy", headerName: "Deleted By", width: 160 },
    {
      field: "deletedAt",
      headerName: "Deleted At",
      width: 200,
      renderCell: (params) => formatDate(params.value),
    },
    { field: "totalRows", headerName: "Records Deleted", width: 140 },
    { field: "ipAddress", headerName: "IP Address", width: 150 },
  ];

  return (
    <Grid container spacing={6}>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={<Typography variant="h6">Deleted Franchise History</Typography>}
            subheader={
              <Breadcrumbs aria-label="Breadcrumb" mt={2}>
                <Typography>Cpanel</Typography>
                <Typography>Franchise</Typography>
                <Typography>Delete History</Typography>
              </Breadcrumbs>
            }
            action={
              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search franchise / deleted by"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/cpanel/tenant/list")}
                >
                  Back
                </Button>
              </Box>
            }
          />
          <CardContent sx={{ height: height - 180 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.tenantDeleteHistoryId}
              rowCount={rowCount}
              loading={isLoading}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[25, 50, 100]}
              disableColumnFilter
              sortingMode="server"
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default DeleteHistory;
